import { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

// One-tap "Install app" button. Uses the native beforeinstallprompt on
// Chrome/Edge/Android; falls back to Add-to-Home-Screen instructions on iOS.
// Renders nothing when already installed or when install isn't available.
export default function InstallAppButton({ variant = "solid", className = "", label = "Install app" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosOpen, setIosOpen] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    if (installed) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [installed]);

  // Show only when we can actually install: native prompt captured, or iOS Safari.
  if (installed) return null;
  if (!deferred && !ios) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else if (ios) {
      setIosOpen(true);
    }
  };

  const styles =
    variant === "ghost"
      ? "border border-white/20 text-white hover:bg-white/10"
      : "btn-brand text-white";

  return (
    <>
      <button
        data-testid="install-app-btn"
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${styles} ${className}`}
      >
        <Download size={15} /> <span className="hidden sm:inline">{label}</span>
      </button>

      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-sm" data-testid="ios-install-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Download size={18} /> Install on your iPhone</DialogTitle>
            <DialogDescription className="text-zinc-400">Add Link Website Pro to your Home Screen in two taps.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 mt-2 text-sm">
            <li className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">1</span>
              <span className="flex items-center gap-1.5 text-zinc-200">Tap the <Share size={15} className="text-[#4da3ff]" /> <b>Share</b> button in Safari.</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">2</span>
              <span className="flex items-center gap-1.5 text-zinc-200">Choose <Plus size={15} className="text-[#4da3ff]" /> <b>Add to Home Screen</b>.</span>
            </li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
